import type { MemberSubsetDraft } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetDraft';
import type { PnlHierarchyOption, PnlSubsetViewSummary } from 'src/components/star-grid/starGridTypes';
import {
  descendantKeys,
  MemberIndex,
  MemberIndexEntry,
  MemberRow,
  MemberSubsetScope,
  topmostKeys
} from 'src/components/star-grid/utils/subset-editor/memberSubset';
import {
  applyOps,
  entryRows,
  excludeEntries,
  flattenEntries,
  scopeEntries,
  SubsetEntry,
  SubsetOp,
  toEntries
} from 'src/components/star-grid/utils/subset-editor/memberSubsetEntries';

export interface DimensionHierarchyPair {
  readonly dimension: string;
  readonly hierarchy: string;
}

// The (dimension, hierarchy) pair, not `hierarchy_id`: an unregistered pair has no id.
export const optionValue = (option: PnlHierarchyOption): string => `${option.dimension ?? ''}::${option.cdp_hierarchy ?? ''}`;

/** The dropdown's choice, or the option the editor was opened with while nothing has been picked. */
export const pickHierarchyOption = (
  options: readonly PnlHierarchyOption[] | undefined,
  pickedValue: string | null,
  hierarchyId: string
): PnlHierarchyOption | null => {
  const list = options ?? [];
  if (pickedValue !== null) return list.find((option) => optionValue(option) === pickedValue) ?? null;
  return list.find((option) => option.hierarchy_id === hierarchyId) ?? null;
};

// Falls back to `<dimension>::<hierarchy>::<attribute>` so callers that pass no options still get aliases and views.
export const aliasPairOf = (
  dimension: string | undefined,
  cdpHierarchy: string | undefined,
  hierarchyId: string
): DimensionHierarchyPair | undefined => {
  if (dimension && cdpHierarchy) return { dimension, hierarchy: cdpHierarchy };
  const parts = hierarchyId.split('::');
  return parts.length >= 2 && parts[0] && parts[1] ? { dimension: parts[0], hierarchy: parts[1] } : undefined;
};

/** The registered display attribute, but only when it is genuinely one of the aliases on offer. */
export const defaultAliasOf = (attribute: string | null | undefined, aliasNames: readonly string[]): string | null =>
  attribute && aliasNames.includes(attribute) ? attribute : null;

/** The caller's own default view first, then the public one. */
export const defaultViewIdOf = (views: readonly PnlSubsetViewSummary[]): number | null => {
  const own = views.find((view) => view.is_default && view.visibility === 'PRIVATE');
  const shared = views.find((view) => view.is_default && view.visibility === 'PUBLIC');
  return (own ?? shared)?.id ?? null;
};

export interface GridRowsInput {
  readonly flat: boolean;
  readonly flatRows: readonly MemberRow[];
  readonly treeRows: readonly MemberRow[];
  readonly savedEntries: readonly SubsetEntry[] | null;
  readonly scope: MemberSubsetScope | null;
  readonly ops: readonly SubsetOp[];
  readonly excluded: ReadonlySet<string>;
  readonly index: MemberIndex;
}

/** Every row the grid is handed, expanded or not; the grid owns expand/collapse over this fixed set. */
export const buildGridRows = ({ flat, flatRows, treeRows, savedEntries, scope, ops, excluded, index }: GridRowsInput): MemberRow[] => {
  let entries: readonly SubsetEntry[] = savedEntries ?? toEntries(flat ? flatRows : treeRows);
  // Keep rebuilds from the hierarchy, so it reads the same whether a saved view is selected or not.
  if (scope !== null) entries = scopeEntries(index, scope);
  if (flat && savedEntries !== null) entries = flattenEntries(entries);
  // Exclusions after the splices, or a `Parent` splice re-introduces a row that was just removed.
  return entryRows(excludeEntries(applyOps(entries, index, ops), excluded));
};

// The draft holds position keys but the grid opens rows by uid; `Parent`'s spliced copies never start open.
export const initiallyExpandedUids = (rows: readonly MemberRow[], expanded: ReadonlySet<string>): Set<string> => {
  const uids = new Set<string>();
  for (const row of rows) if (row.added !== true && expanded.has(row.key)) uids.add(row.uid);
  return uids;
};

/** Distinct MEMBERS, not positions: a multi-parent member ticked under two parents is still one. */
export const distinctMemberCount = (index: MemberIndex, keys: readonly string[]): number => {
  const ids = new Set<string>();
  for (const key of keys) {
    const id = index.get(key)?.id;
    if (id) ids.add(id);
  }
  return ids.size;
};

/** Selected members with no ticked position among the rows, e.g. left off screen by a saved view or a Keep. */
export const membersNotInView = (index: MemberIndex, keys: readonly string[], rows: readonly MemberRow[]): number => {
  const inView = new Set(rows.map((row) => row.key));
  return (
    distinctMemberCount(index, keys) -
    distinctMemberCount(
      index,
      keys.filter((key) => inView.has(key))
    )
  );
};

/** What OK hands back: member ids, deduped, in hierarchy order unless the caller wants tick order. */
export const confirmedMemberIds = (index: MemberIndex, keys: readonly string[], pickOrder: boolean): string[] => {
  const ordered = keys
    .map((key) => index.get(key))
    .filter((entry): entry is MemberIndexEntry => entry !== undefined)
    .sort((left, right) => (pickOrder ? 0 : left.order - right.order))
    .map((entry) => entry.id);
  return [...new Set(ordered)];
};

/** Every openable position beneath the selection, or beneath every shown row when nothing is ticked. */
export const expandAllKeys = (index: MemberIndex, checked: ReadonlySet<string>, rows: readonly MemberRow[]): string[] => {
  const anchors = checked.size > 0 ? topmostKeys(index, [...checked]) : rows.map((row) => row.key);
  const keys = new Set<string>();
  // Leaves are skipped: an expanded key with nothing under it would keep Reset View enabled forever.
  const openable = (key: string): boolean => index.get(key)?.isLeaf === false;
  for (const anchor of anchors) {
    if (openable(anchor)) keys.add(anchor);
    for (const key of descendantKeys(index, anchor)) if (openable(key)) keys.add(key);
  }
  return [...keys];
};

/** Every position beneath the selection, or everything currently open when nothing is ticked. */
export const collapseAllKeys = (index: MemberIndex, checked: ReadonlySet<string>, expanded: ReadonlySet<string>): string[] => {
  if (checked.size === 0) return [...expanded];
  const keys = new Set<string>();
  for (const anchor of topmostKeys(index, [...checked])) {
    keys.add(anchor);
    for (const key of descendantKeys(index, anchor)) keys.add(key);
  }
  return [...keys];
};

/** Reset View has nothing to undo: the alias and saved view count too, because both live outside the draft. */
export const isResetViewDisabled = (
  draft: MemberSubsetDraft,
  isSearching: boolean,
  alias: string | null,
  viewId: number | null,
  openingViewId: number | null
): boolean =>
  draft.scope === null &&
  !draft.flat &&
  draft.checked.size === 0 &&
  draft.expanded.size === 0 &&
  draft.excluded.size === 0 &&
  draft.ops.length === 0 &&
  !isSearching &&
  alias === null &&
  viewId === openingViewId;

interface WalkedNode {
  readonly data?: MemberRow;
  readonly expanded: boolean;
  isSelected(): boolean | undefined;
}

/** The slice of `GridApi` these helpers read, so they can be exercised without a grid. */
export interface NodeWalker {
  forEachNode(callback: (node: WalkedNode) => void): void;
}

// Read per grid NODE: `Parent` and drill can put one position on screen twice, so a key cannot name the row.
export const selectedRowUids = (api: NodeWalker | null): string[] => {
  const uids: string[] = [];
  api?.forEachNode((node) => {
    if (node.data && node.isSelected() === true) uids.push(node.data.uid);
  });
  return uids;
};

export const expandedRowUids = (api: NodeWalker | null): Set<string> => {
  const uids = new Set<string>();
  api?.forEachNode((node) => {
    if (node.data && node.expanded === true) uids.add(node.data.uid);
  });
  return uids;
};

/** Split for Remove: a collapsed row takes its hidden descendants, an expanded one goes alone. */
export const splitByExpansion = (uids: readonly string[], expanded: ReadonlySet<string>): { collapsed: string[]; opened: string[] } => ({
  collapsed: uids.filter((uid) => !expanded.has(uid)),
  opened: uids.filter((uid) => expanded.has(uid))
});
